import { useId, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Alert, AlertTitle, Box, Button, Typography } from '@mui/material'
import { Sheet, ActionBar } from '../../components'
import { cancelReopen } from './publishApi'

// "for review by daniel on 11/3/2026, 8:10 PM": how a reopened piece came
// back (the server's `republish`: mode, reopenedAt, reopenedBy).
export function republishLine(t, republish, language) {
    const parts = [t(`photoReview.republish.modes.${republish?.mode === 'reshoot' ? 'reshoot' : 'review'}`)]
    if (republish?.reopenedBy) parts.push(t('photoReview.republish.by', { name: republish.reopenedBy }))
    if (republish?.reopenedAt) {
        const when = new Date(republish.reopenedAt).toLocaleString(language, { dateStyle: 'short', timeStyle: 'short' })
        parts.push(t('photoReview.republish.on', { when }))
    }
    return parts.join(' ')
}

// "Republish — replaces the live photos": on a reopened piece, in review and
// in its preview. With `fetchResource`, it also carries Cancel reopen, which
// puts every shot back as published (the site was never touched); the server
// refuses once the republish has started or the shots moved on, and its
// reason is shown as is.
export default function RepublishNotice({ sku, republish, fetchResource, onCancelled }) {
    const { t, i18n } = useTranslation()
    const titleId = useId()
    const [asking, setAsking] = useState(false)
    const [cancelling, setCancelling] = useState(false)
    const [refusal, setRefusal] = useState(null)
    const canCancel = Boolean(fetchResource) && !republish.started

    async function handleCancel() {
        if (cancelling) return
        setCancelling(true)
        setRefusal(null)
        const response = await cancelReopen(fetchResource, sku)
        setCancelling(false)
        setAsking(false)
        if (response?.error) setRefusal(response)
        else onCancelled?.(sku)
    }

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            <Alert
                severity='warning'
                role='region'
                aria-labelledby={titleId}
                // On a phone the action takes its own line under the text,
                // lined up with it past the icon, instead of squeezing it.
                sx={{
                    flexWrap: { xs: 'wrap', sm: 'nowrap' },
                    '& .MuiAlert-message': { flex: 1, minWidth: 0 },
                    '& .MuiAlert-action': { flexBasis: { xs: '100%', sm: 'auto' }, ml: { xs: 0, sm: 'auto' }, pl: { xs: '34px', sm: 2 }, pt: { xs: 0, sm: '4px' } },
                }}
                action={canCancel && (
                    <Button color='inherit' size='small' disabled={cancelling} onClick={() => setAsking(true)} sx={{ whiteSpace: 'nowrap' }}>
                        {t('photoReview.republish.cancel')}
                    </Button>
                )}
            >
                <AlertTitle id={titleId} sx={{ fontWeight: 600 }}>{t('photoReview.republish.title')}</AlertTitle>
                <Typography variant='body2'>
                    {t('photoReview.republish.reopened', { how: republishLine(t, republish, i18n.language) })}
                    {' '}
                    {t('photoReview.republish.untouched')}
                </Typography>
                {republish.started && <Typography variant='body2' sx={{ mt: 0.5 }}>{t('photoReview.republish.started')}</Typography>}
            </Alert>
            {refusal && (
                <Alert severity='error' onClose={() => setRefusal(null)}>
                    <Typography variant='body2' sx={{ fontWeight: 600 }}>
                        {refusal.republishStarted ? t('photoReview.republish.started') : t('photoReview.republish.cancelFailed')}
                    </Typography>
                    <Typography variant='body2'>{refusal.error}</Typography>
                </Alert>
            )}
            <Sheet
                open={asking}
                onClose={() => { if (!cancelling) setAsking(false) }}
                title={t('photoReview.republish.cancelTitle', { sku })}
                actions={(
                    <ActionBar
                        cancel={{ label: t('photoReview.republish.keep'), onClick: () => setAsking(false), disabled: cancelling }}
                        confirm={{ label: t('photoReview.republish.cancelConfirm'), tone: 'danger', onClick: handleCancel, disabled: cancelling }}
                    />
                )}
            >
                <Typography>{t('photoReview.republish.cancelBody')}</Typography>
            </Sheet>
        </Box>
    )
}
